import { useState } from 'react';
import { CheckCircle2, RefreshCcw, XCircle } from 'lucide-react';
import { useTranslation } from '@eon/i18n';
import { Badge, Button, Card, CardContent, CardHeader, CardTitle, Label } from '@eon/ui';
import { cn, formatRelative } from '@eon/utils';
import type { CallLogEntry } from './types';

function pretty(value: unknown): string {
  if (value === undefined || value === null) return '';
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
}

export function ResponsePanel({
  log,
  onClear,
}: {
  log: CallLogEntry[];
  onClear: () => void;
}) {
  const { t } = useTranslation();
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const last = log[0];
  const expanded = log.find((e) => e.id === expandedId) ?? last;

  return (
    <div className="space-y-4">
      <Card className="sticky top-4">
        <CardHeader>
          <CardTitle className="text-base">{t('deviceControl.response.title')}</CardTitle>
        </CardHeader>
        <CardContent>
          {last ? (
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                {last.error ? (
                  <Badge variant="destructive" className="gap-1">
                    <XCircle className="h-3 w-3" /> {t('deviceControl.response.failed')}
                  </Badge>
                ) : (
                  <Badge variant="success" className="gap-1">
                    <CheckCircle2 className="h-3 w-3" /> {t('deviceControl.response.ok')}
                  </Badge>
                )}
                <span className="text-sm font-medium">{t(last.actionLabelKey)}</span>
                <span className="text-muted-foreground ml-auto text-xs">{last.ms}ms</span>
              </div>
              <div>
                <Label className="text-xs">{t('deviceControl.response.request')}</Label>
                <pre className="bg-muted/50 mt-1 max-h-40 overflow-auto rounded p-2 text-[11px]">
                  {pretty(last.request)}
                </pre>
              </div>
              <div>
                <Label className="text-xs">
                  {last.error ? t('deviceControl.response.error') : t('deviceControl.response.response')}
                </Label>
                <pre
                  className={cn(
                    'mt-1 max-h-60 overflow-auto rounded p-2 text-[11px]',
                    last.error ? 'bg-destructive/10 text-destructive' : 'bg-muted/50',
                  )}
                >
                  {last.error ?? pretty(last.response)}
                </pre>
              </div>
            </div>
          ) : (
            <p className="text-muted-foreground text-sm">{t('deviceControl.response.empty')}</p>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-base">{t('deviceControl.history.title')}</CardTitle>
          {log.length ? (
            <Button variant="ghost" size="sm" onClick={onClear}>
              <RefreshCcw className="mr-1.5 h-3.5 w-3.5" /> {t('deviceControl.history.clear')}
            </Button>
          ) : null}
        </CardHeader>
        <CardContent>
          {log.length === 0 ? (
            <p className="text-muted-foreground text-sm">{t('deviceControl.history.empty')}</p>
          ) : (
            <ul className="divide-border divide-y text-sm">
              {log.map((e) => {
                const isOpen = expandedId === e.id;
                return (
                  <li key={e.id} className="py-2">
                    <button
                      type="button"
                      className="flex w-full items-center gap-2 text-left"
                      onClick={() => setExpandedId(isOpen ? null : e.id)}
                    >
                      {e.error ? (
                        <XCircle className="text-destructive h-3.5 w-3.5 shrink-0" />
                      ) : (
                        <CheckCircle2 className="text-emerald-500 h-3.5 w-3.5 shrink-0" />
                      )}
                      <span className="flex-1 truncate font-medium">{t(e.actionLabelKey)}</span>
                      <span className="text-muted-foreground text-xs">{e.ms}ms</span>
                      <span className="text-muted-foreground text-xs">
                        {formatRelative(new Date(e.ts).toISOString())}
                      </span>
                    </button>
                    {isOpen && expanded?.id === e.id ? (
                      <div className="mt-2 space-y-2 pl-6">
                        <pre className="bg-muted/50 max-h-32 overflow-auto rounded p-2 text-[10px]">
                          {pretty(e.request)}
                        </pre>
                        <pre
                          className={cn(
                            'max-h-40 overflow-auto rounded p-2 text-[10px]',
                            e.error ? 'bg-destructive/10 text-destructive' : 'bg-muted/50',
                          )}
                        >
                          {e.error ?? pretty(e.response)}
                        </pre>
                      </div>
                    ) : null}
                  </li>
                );
              })}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
